import React from 'react';
import moment from 'moment';
import find from 'lodash/find';
import findLast from 'lodash/findLast';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'common/i18n/hooks';
import { InputLabelReact } from 'common/components/input/label';
import { InputReact } from 'common/components/input';
import { Experiment, ExperimentEndConditionType, ExperimentStatus, ExperimentTestingModel } from 'apps/experiments/src/types';
import { ButtonSize } from 'common/components/button/constants';
import { InputSize, InputType } from 'common/components/input/types';
import { MAX_CONFIDENCE_LEVEL, MAX_END_AFTER_DAYS_SIZE, MIN_CONFIDENCE_LEVEL, MIN_END_AFTER_DAYS_SIZE, MIN_SAMPLE_SIZE } from 'apps/experiments/src/components/utils';
import { DateRangePickerReact } from 'common/widgets/date-range-picker-new';
import { SelectReact } from 'common/components/select';
import { DateRangeType, DisplayContext } from 'common/widgets/date-range-picker/models/types';
import { SelectTheme } from 'common/components/select/constants';
import { ExperimentCompletedDateRange, ExperimentDateRange } from 'common/widgets/date-range-picker/models/experiment-date-range';
import { useExperimentPageContext } from 'iron/apps/experiments/src/components/context';
import { eventDetectionQueryKey } from 'apps/experiments/src/react-query';
import { queryModule } from 'common/report/report-manager/query';
import { BookmarkType } from 'common/report/queries/app-api/bookmark-client';
import { getLocation } from 'common/util/url';
import { formatDate } from 'common/util/date';
import { useQueryFeatureFlags } from 'iron/apps/experiments/src/components/hooks/query-feature-flag-list';
import { createEventDetectionBookmarkParams } from 'iron/apps/experiments/src/components/query-utils';
import { useExperimentSettings } from 'iron/apps/experiments/src/components/hooks/experiment-settings';
import { useStagedExperiment } from 'iron/apps/experiments/src/components/hooks/staged-experiment';
import styles from './index.module.styl';
import { CollectionMethodComponents } from './collection-method';
import { ExperimentPageConfigurationSection } from './_section';
import { ExperimentPageConfigurationSetting } from './_setting';
const DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss";
export function ExperimentPageConfiguration() {
  var _stagedExperiment$set, _EXPERIMENT_END_CONDI, _stagedExperiment$set3, _stagedExperiment$set4, _stagedExperiment$set5, _stagedExperiment$set6, _TESTING_MODELS$find, _stagedExperiment$set8;
  const [isDateOpen, setIsDateOpen] = React.useState(false);
  const i18n = useTranslation();
  const {
    pathname,
    search,
    hash
  } = getLocation();
  const {
    stagedExperiment,
    stageChanges
  } = useStagedExperiment();
  const {
    stagedFeatureFlag
  } = useQueryFeatureFlags();
  const {
    projectId,
    workspaceId
  } = useExperimentPageContext();
  const experimentSettings = useExperimentSettings();
  const [isEditing, setIsEditing] = React.useState(false);
  const eventDetectionBookmarkParams = React.useMemo(() => {
    var _experimentSettings$n;
    if (!stagedExperiment) {
      return undefined;
    }
    return createEventDetectionBookmarkParams({
      experiment: stagedExperiment as Experiment,
      featureFlag: stagedFeatureFlag,
      experimentNameProperty: (_experimentSettings$n = experimentSettings.nameProperty) === null || _experimentSettings$n === void 0 ? void 0 : _experimentSettings$n.value
    });
  }, [stagedExperiment, stagedFeatureFlag, experimentSettings]);
  const {
    data: eventDetectionQueryResults
  } = useQuery({
    queryKey: eventDetectionQueryKey({
      projectId: projectId!,
      workspaceId: workspaceId!,
      experimentId: stagedExperiment.id,
      bookmarkParams: eventDetectionBookmarkParams
    }),
    enabled: !!eventDetectionBookmarkParams,
    queryFn: () => {
      const queryParams: {
        project_id: string;
        workspace_id?: string;
      } = {
        project_id: String(projectId)
      };
      if (workspaceId) {
        queryParams["workspace_id"] = String(workspaceId);
      }
      return queryModule.executeReportQuery({
        endpoint: BookmarkType.Insights,
        queryParams,
        body: {
          bookmark: eventDetectionBookmarkParams,
          use_query_cache: true,
          use_query_sampling: true,
          report_query_origin: BookmarkType.Insights,
          tracking_props: {}
        },
        throwOnInvalidStatus: true,
        location: {
          pathname,
          search,
          hash,
          key: "",
          state: ""
        }
      }).then(response => {
        return response.json();
      });
    }
  });
  const eventDates = React.useMemo(() => {
    var _Object$values$, _Object$values, _find, _findLast;
    if (!eventDetectionQueryResults) {
      return [undefined, undefined];
    }
    const dateCounts = (_Object$values$ = (_Object$values = Object.values(eventDetectionQueryResults === null || eventDetectionQueryResults === void 0 ? void 0 : eventDetectionQueryResults.series)) === null || _Object$values === void 0 ? void 0 : _Object$values[0]) !== null && _Object$values$ !== void 0 ? _Object$values$ : {};
    const dateCountEntries = Object.entries(dateCounts).sort((a, b) => {
      if (a[0] < b[0]) {
        return -1;
      }
      if (a[0] > b[0]) {
        return 1;
      }
      return 0;
    });
    return [(_find = find(dateCountEntries, entry => entry[1] > 0)) === null || _find === void 0 ? void 0 : _find[0], (_findLast = findLast(dateCountEntries, entry => entry[1] > 0)) === null || _findLast === void 0 ? void 0 : _findLast[0]];
  }, [eventDetectionQueryResults]);
  const showDateRange = React.useMemo(() => {
    var _stagedExperiment$sta;
    return [ExperimentStatus.CONCLUDED, ExperimentStatus.SUCCESS, ExperimentStatus.FAIL].includes((_stagedExperiment$sta = stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.status) !== null && _stagedExperiment$sta !== void 0 ? _stagedExperiment$sta : ExperimentStatus.DRAFT);
  }, [stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.status]);
  const showStartDate = React.useMemo(() => {
    if (showDateRange) {
      return false;
    }
    if ((stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.status) !== ExperimentStatus.DRAFT) {
      return true;
    }
    return Boolean(eventDates[0]);
  }, [showDateRange, stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.status, eventDates]);
  const showEndCondition = React.useMemo(() => {
    var _stagedExperiment$sta2;
    return [ExperimentStatus.DRAFT, ExperimentStatus.ACTIVE].includes((_stagedExperiment$sta2 = stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.status) !== null && _stagedExperiment$sta2 !== void 0 ? _stagedExperiment$sta2 : ExperimentStatus.DRAFT);
  }, [stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.status]);
  const experimentDateRange = React.useMemo(() => {
    return new ExperimentDateRange({
      type: DateRangeType.On,
      from: stagedExperiment !== null && stagedExperiment !== void 0 && stagedExperiment.start_date ? moment(stagedExperiment.start_date).format(DATE_FORMAT) : ""
    });
  }, [stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.start_date]);
  const experimentCompletedDateRange = React.useMemo(() => {
    return new ExperimentCompletedDateRange({
      type: DateRangeType.Between,
      from: stagedExperiment !== null && stagedExperiment !== void 0 && stagedExperiment.start_date ? moment(stagedExperiment.start_date).format(DATE_FORMAT) : "",
      to: stagedExperiment !== null && stagedExperiment !== void 0 && stagedExperiment.end_date ? moment(stagedExperiment.end_date).format(DATE_FORMAT) : ""
    });
  }, [stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.start_date, stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.end_date]);
  React.useEffect(() => {
    if (!(stagedExperiment !== null && stagedExperiment !== void 0 && stagedExperiment.start_date) && !!eventDates[0]) {
      const updates: Partial<Experiment> = {
        start_date: moment(eventDates[0]).format(DATE_FORMAT)
      };
      if (showDateRange) {
        updates.end_date = moment(eventDates[1]).format(DATE_FORMAT);
      }
      stageChanges(updates);
    }
  }, [stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.start_date, showDateRange, eventDates, stageChanges]);
  const TESTING_MODELS = [{
    label: i18n.t("experiments.configuration.testing-model-options.frequentist"),
    value: ExperimentTestingModel.FREQUENTIST,
    tooltipLabel: i18n.t("experiments.configuration.testing-model-options.frequentist-info")
  }, {
    label: i18n.t("experiments.configuration.testing-model-options.sequential"),
    value: ExperimentTestingModel.SEQUENTIAL,
    tooltipLabel: i18n.t("experiments.configuration.testing-model-options.sequential-info")
  }];
  const EXPERIMENT_END_CONDITIONS = [{
    label: i18n.t("experiments.configuration.end-condition-options.sample-size"),
    value: ExperimentEndConditionType.SAMPLE_SIZE,
    tooltipLabel: i18n.t("experiments.configuration.end-condition-options.sample-size-info")
  }, {
    label: i18n.t("experiments.configuration.end-condition-options.days"),
    value: ExperimentEndConditionType.DAYS,
    tooltipLabel: i18n.t("experiments.configuration.end-condition-options.days-info")
  }];
  const CollectionMethodComponent = CollectionMethodComponents[(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set = stagedExperiment.settings) === null || _stagedExperiment$set === void 0 ? void 0 : _stagedExperiment$set.collectionMethod) as keyof typeof CollectionMethodComponents];
  return <aside className={styles.configuration} data-sentry-component="ExperimentPageConfiguration" data-sentry-source-file="index.tsx">
      <div className={styles.content}>
        <ExperimentPageConfigurationSection label={i18n.t("experiments.configuration.details")} btnIcon={isEditing ? "eye-on" : "edit"} onButtonClick={() => setIsEditing(!isEditing)} data-sentry-element="ExperimentPageConfigurationSection" data-sentry-source-file="index.tsx">
          {CollectionMethodComponent && <CollectionMethodComponent isEditing={isEditing} />}
        </ExperimentPageConfigurationSection>

        <ExperimentPageConfigurationSection label={i18n.t("experiments.configuration.analysis")} data-sentry-element="ExperimentPageConfigurationSection" data-sentry-source-file="index.tsx">
          {showDateRange && <ExperimentPageConfigurationSetting label={i18n.t("experiments.configuration.date")} helpText={i18n.t("experiments.configuration.date-info")}>
              <SelectReact className={styles.fullWidth} theme={SelectTheme.QbSubitem} size={ButtonSize.Small} alwaysShowCaret label={"".concat(formatDate(moment(experimentDateRange.from as string)), " - ").concat(formatDate(moment(experimentDateRange.to as string)))} onMenuChange={() => setIsDateOpen(!isDateOpen)} open={isDateOpen}>
                <div slot='body'>
                  <DateRangePickerReact singlePickerMode isTimePickerEnabled={false} displayContext={DisplayContext.ExperimentCompletedTimePicker} dateRange={experimentCompletedDateRange.getAttributes()} onApply={ev => {
                stageChanges({
                  start_date: ev.detail.dateRange.from ? moment(ev.detail.dateRange.from.toString()).format(DATE_FORMAT) : null,
                  end_date: ev.detail.dateRange.to ? moment(ev.detail.dateRange.to.toString()).format(DATE_FORMAT) : null
                });
                setIsDateOpen(false);
              }} onCancel={() => {
                setIsDateOpen(false);
              }} />
                </div>
              </SelectReact>
            </ExperimentPageConfigurationSetting>}

          {showStartDate && <div>
              <InputLabelReact fieldId='experiment-start' label={i18n.t("experiments.configuration.start-date")} helpText={i18n.t("experiments.configuration.start-date-info")} />
              <SelectReact className={styles.fullWidth} theme={SelectTheme.QbSubitem} size={ButtonSize.Small} alwaysShowCaret label={experimentDateRange.from ? formatDate(moment(experimentDateRange.from as string)) || "" : ""} onMenuChange={() => setIsDateOpen(!isDateOpen)} open={isDateOpen}>
                <div slot='body'>
                  <DateRangePickerReact singlePickerMode displayContext={DisplayContext.ExperimentTimePicker} dateRange={experimentDateRange.getAttributes()} onApply={ev => {
                stageChanges({
                  start_date: ev.detail.dateRange.from ? moment(ev.detail.dateRange.from.toString()).format(DATE_FORMAT) : null
                });
                setIsDateOpen(false);
              }} onCancel={() => {
                setIsDateOpen(false);
              }} />
                </div>
              </SelectReact>
            </div>}

          {showEndCondition && <ExperimentPageConfigurationSetting label={i18n.t("experiments.configuration.end-condition")} helpText={i18n.t("experiments.configuration.end-condition-info")}>
              <div className={styles.endConditionWrapper}>
                <SelectReact className={styles.endConditionType} theme={SelectTheme.QbSubitem} size={ButtonSize.Small} selectedOption={(_EXPERIMENT_END_CONDI = EXPERIMENT_END_CONDITIONS.find(condition => {
              var _stagedExperiment$set2;
              return condition.value === (stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set2 = stagedExperiment.settings) === null || _stagedExperiment$set2 === void 0 ? void 0 : _stagedExperiment$set2.endCondition);
            })) === null || _EXPERIMENT_END_CONDI === void 0 ? void 0 : _EXPERIMENT_END_CONDI.label} disableSearch alwaysShowCaret menuWidth={300} sections={[{
              items: EXPERIMENT_END_CONDITIONS.map(condition => ({
                key: condition.value,
                label: condition.label,
                wrapSubLabel: true,
                subLabel: condition.tooltipLabel,
                value: condition.value
              }))
            }]} onChange={(ev: TODO) => stageChanges({
              settings: {
                endCondition: ev.detail.item.value
              }
            })} />
                {(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set3 = stagedExperiment.settings) === null || _stagedExperiment$set3 === void 0 ? void 0 : _stagedExperiment$set3.endCondition) === ExperimentEndConditionType.SAMPLE_SIZE && <InputReact className={styles.endConditionValue} fieldId='experiment-end-condition-sample-size' placeholder={i18n.t("experiments.configuration.end-condition-options.sample-size-placeholder")} value={String(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set4 = stagedExperiment.settings) === null || _stagedExperiment$set4 === void 0 ? void 0 : _stagedExperiment$set4.sampleSize)} type={InputType.Number} size={InputSize.Small} min={String(MIN_SAMPLE_SIZE)} step='1' onInput={ev => {
              stageChanges({
                settings: {
                  sampleSize: Number(ev.target.value)
                }
              });
            }} onChange={(ev: TODO) => {
              stageChanges({
                settings: {
                  sampleSize: Number(ev.target.value)
                }
              });
            }} />}
                {(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set5 = stagedExperiment.settings) === null || _stagedExperiment$set5 === void 0 ? void 0 : _stagedExperiment$set5.endCondition) === ExperimentEndConditionType.DAYS && <InputReact className={styles.endConditionValue} fieldId='experiment-end-condition-days' placeholder={i18n.t("experiments.configuration.end-condition-options.days-placeholder")} value={String(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set6 = stagedExperiment.settings) === null || _stagedExperiment$set6 === void 0 ? void 0 : _stagedExperiment$set6.endAfterDays)} type={InputType.Number} size={InputSize.Small} min={String(MIN_END_AFTER_DAYS_SIZE)} max={String(MAX_END_AFTER_DAYS_SIZE)} step='1' onInput={ev => {
              stageChanges({
                settings: {
                  endAfterDays: Number(ev.target.value)
                }
              });
            }} onChange={(ev: TODO) => {
              stageChanges({
                settings: {
                  endAfterDays: Number(ev.target.value)
                }
              });
            }} />}
              </div>
            </ExperimentPageConfigurationSetting>}

          <ExperimentPageConfigurationSetting label={i18n.t("experiments.configuration.testing-model")} data-sentry-element="ExperimentPageConfigurationSetting" data-sentry-source-file="index.tsx">
            <SelectReact className={styles.fullWidth} theme={SelectTheme.QbSubitem} size={ButtonSize.Small} selectedOption={(_TESTING_MODELS$find = TESTING_MODELS.find(condition => {
            var _stagedExperiment$set7;
            return condition.value === (stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set7 = stagedExperiment.settings) === null || _stagedExperiment$set7 === void 0 ? void 0 : _stagedExperiment$set7.testingModel);
          })) === null || _TESTING_MODELS$find === void 0 ? void 0 : _TESTING_MODELS$find.label} disableSearch alwaysShowCaret menuWidth={300} sections={[{
            items: TESTING_MODELS.map(testingModel => ({
              key: testingModel.value,
              label: testingModel.label,
              wrapSubLabel: true,
              subLabel: testingModel.tooltipLabel,
              value: testingModel.value
            }))
          }]} onChange={(ev: TODO) => stageChanges({
            settings: {
              testingModel: ev.detail.item.value
            }
          })} data-sentry-element="SelectReact" data-sentry-source-file="index.tsx" />
          </ExperimentPageConfigurationSetting>

          <ExperimentPageConfigurationSetting label={i18n.t("experiments.configuration.confidence-level")} helpText={i18n.t("experiments.configuration.confidence-level-info")} data-sentry-element="ExperimentPageConfigurationSetting" data-sentry-source-file="index.tsx">
            <InputReact className={styles.fullWidth} fieldId='experiment-confidence-level' value={String(stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set8 = stagedExperiment.settings) === null || _stagedExperiment$set8 === void 0 ? void 0 : _stagedExperiment$set8.confidenceLevel)} type={InputType.Number} size={InputSize.Small} min={String(MIN_CONFIDENCE_LEVEL)} max={String(MAX_CONFIDENCE_LEVEL)} step='0.01' onInput={ev => {
            stageChanges({
              settings: {
                confidenceLevel: Number(ev.target.value)
              }
            });
          }} onChange={(ev: TODO) => {
            stageChanges({
              settings: {
                confidenceLevel: Number(ev.target.value)
              }
            });
          }} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
          </ExperimentPageConfigurationSetting>
        </ExperimentPageConfigurationSection>
      </div>
    </aside>;
}